import { io, type Socket } from 'socket.io-client';
import type { VoteValue, Session, VoteSummary, Participant } from '../types/index';
import { WS_URL } from '../utils/constants';

export interface SocketEvents {
  // Client -> Server
  join_session: (data: { sessionId: string; name: string }) => void;
  cast_vote: (data: { sessionId: string; value: VoteValue }) => void;
  show_votes: (data: { sessionId: string }) => void;
  reset_votes: (data: { sessionId: string }) => void;
  reopen_voting: (data: { sessionId: string }) => void;

  // Server -> Client
  session_joined: (data: { session: Session; isFacilitator: boolean }) => void;
  session_state: (data: {
    state: 'collecting' | 'revealed';
    participants: Participant[];
    expiresAt: number;
    story?: string;
    voteStartTime: number;
  }) => void;
  vote_ack: (data: { self: { voted: boolean; value?: VoteValue } }) => void;
  votes_revealed: (data: {
    votes: Array<{ name: string; value: VoteValue }>;
    summary: VoteSummary;
  }) => void;
  votes_reset: (data: { state: 'collecting' }) => void;
  voting_reopened: (data: { state: 'collecting' }) => void;
  session_expired: (data: { reason: string }) => void;
  error: (data: { message: string; code: string }) => void;
}

class SocketService {
  private socket: Socket | null = null;
  private connecting: boolean = false;
  private currentFacilitatorToken: string | undefined = undefined;

  connect(facilitatorToken?: string): Socket {
    // If socket exists and is connected, and we have the same facilitatorToken (or both are undefined), reuse it
    if (this.socket?.connected) {
      if (facilitatorToken === this.currentFacilitatorToken) {
        return this.socket;
      }
      // If facilitatorToken changed, we need to reconnect
      if (facilitatorToken !== this.currentFacilitatorToken) {
        this.disconnect();
      }
    }

    // If we're already in the process of connecting, return existing socket
    if (this.connecting && this.socket) {
      return this.socket;
    }

    // If socket exists but not connected, clean it up first
    if (this.socket) {
      this.socket.removeAllListeners();
      this.socket.disconnect();
      this.socket = null;
    }

    this.connecting = true;
    this.currentFacilitatorToken = facilitatorToken;
    this.socket = io(WS_URL, {
      auth: facilitatorToken ? { facilitatorToken } : undefined,
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      reconnectionAttempts: Infinity, // Keep trying to reconnect
      // Prevent multiple connection attempts
      autoConnect: true,
      // Connection timeout
      timeout: 20000, // Match server pingTimeout
      // Prefer WebSocket transport - more efficient and better for long-lived connections
      // Fall back to polling if WebSocket fails
      transports: ['websocket', 'polling'],
      // Upgrade to WebSocket automatically
      upgrade: true,
      // Force WebSocket if available (better for proxies/load balancers)
      forceNew: false, // Reuse existing connection if available
      // Note: pingTimeout and pingInterval are server-side only
      // The client automatically adapts to the server's ping/pong settings (10s interval, 20s timeout)
    });

    // Mark connecting as false once connected or disconnected
    this.socket.once('connect', () => {
      this.connecting = false;
    });

    this.socket.once('disconnect', () => {
      this.connecting = false;
    });

    return this.socket;
  }

  disconnect(): void {
    if (this.socket) {
      this.socket.removeAllListeners();
      this.socket.disconnect();
      this.socket = null;
      this.connecting = false;
      this.currentFacilitatorToken = undefined;
    }
  }

  getSocket(): Socket | null {
    return this.socket;
  }

  isConnected(): boolean {
    return this.socket?.connected || false;
  }

  on<K extends keyof SocketEvents>(
    event: K,
    callback: SocketEvents[K],
  ): void {
    if (this.socket) {
      this.socket.on(event, callback as any);
    }
  }

  off<K extends keyof SocketEvents>(
    event: K,
    callback?: SocketEvents[K],
  ): void {
    if (this.socket) {
      this.socket.off(event, callback as any);
    }
  }

  emit<K extends keyof SocketEvents>(
    event: K,
    ...args: Parameters<SocketEvents[K]>
  ): void {
    if (this.socket) {
      this.socket.emit(event, ...args);
    }
  }
}

export const socketService = new SocketService();

